import React, { useState } from 'react';
import axios from 'axios';

/**
 * Confirm + send the same open/close command to many gates at once.
 *
 * `apiBase` is "customer" or "admin"; the modal posts to
 * `/api/<apiBase>/gates/commands` with `{gate_ids, type}`.
 *
 * After the call returns it shows a per-gate result list so the user
 * sees which gates queued and which were skipped (offline, motion in
 * progress, etc.). The parent decides when to dismiss via `onClose`.
 */
function BulkCommandModal({
  gateIds,
  gates,
  commandType,
  apiBase = 'customer',
  onClose,
  onCompleted,
}) {
  const [phase, setPhase] = useState('confirm');
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const verb = commandType === 'open' ? 'לפתוח' : 'לסגור';
  const verbDone = commandType === 'open' ? 'פתיחה' : 'סגירה';
  const count = gateIds.length;

  const send = async () => {
    setPhase('sending');
    setError(null);
    try {
      const res = await axios.post(`/api/${apiBase}/gates/commands`, {
        gate_ids: gateIds,
        type: commandType,
      });
      setResults(res.data.results || []);
      setSummary(res.data.summary || null);
      setPhase('done');
      if (onCompleted) onCompleted(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שליחה נכשלה');
      setPhase('error');
    }
  };

  const nameFor = (gateId, fallback) => {
    if (gates) {
      const g = gates.find((x) => x.id === gateId);
      if (g) return g.name;
    }
    return fallback || `#${gateId}`;
  };

  const labelForStatus = (status) => {
    if (status === 'queued') return { text: '✓ נשלח', color: '#16a34a' };
    if (status === 'offline') return { text: '⚠ לא מחובר', color: '#f59e0b' };
    if (status === 'not_permitted') return { text: '⚠ לא ניתן כעת', color: '#f59e0b' };
    return { text: '✗ נכשל', color: '#dc2626' };
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 60,
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        borderRadius: '10px',
        padding: '1.25rem',
        minWidth: '340px',
        maxWidth: '92vw',
        maxHeight: '85vh',
        overflowY: 'auto',
        zIndex: 61,
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
      }}>
        <h3 style={{ margin: 0, marginBottom: '0.6rem', fontSize: '1.05rem', fontWeight: 600 }}>
          {phase === 'done' ? `${verbDone} — סיכום` : `${verb} ${count} ${count === 1 ? 'שער' : 'שערים'}?`}
        </h3>

        {phase === 'confirm' && (
          <>
            <p style={{ margin: 0, marginBottom: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
              הפקודה תישלח לכל השערים שנבחרו. שערים שלא מחוברים או באמצע תנועה יוצגו ברשימה כדילוג.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={onClose} style={btnGhost}>ביטול</button>
              <button onClick={send} style={btnPrimary(commandType)}>
                {commandType === 'open' ? 'פתח' : 'סגור'}
              </button>
            </div>
          </>
        )}

        {phase === 'sending' && (
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151' }}>שולח פקודות…</p>
        )}

        {phase === 'error' && (
          <>
            <p style={{ margin: 0, marginBottom: '0.85rem', fontSize: '0.9rem', color: '#dc2626' }}>
              {error}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnGhost}>סגור</button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            {summary && (
              <p style={{ margin: 0, marginBottom: '0.85rem', fontSize: '0.85rem', color: '#6b7280' }}>
                נשלחו {summary.queued} מתוך {summary.queued + summary.skipped}
                {summary.skipped > 0 ? ` · דולגו ${summary.skipped}` : ''}
              </p>
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '0.85rem' }}>
              {results.map((r) => {
                const pill = labelForStatus(r.status);
                return (
                  <li
                    key={r.gate_id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid #f3f4f6',
                      fontSize: '0.88rem',
                    }}
                  >
                    <span>{nameFor(r.gate_id, r.gate_name)}</span>
                    <span style={{ color: pill.color, fontWeight: 500 }}>{pill.text}</span>
                  </li>
                );
              })}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnGhost}>סגור</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const btnGhost = {
  padding: '0.45rem 0.9rem',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  borderRadius: '6px',
  fontSize: '0.85rem',
  cursor: 'pointer',
};

const btnPrimary = (cmd) => ({
  padding: '0.45rem 0.9rem',
  border: 'none',
  background: cmd === 'open' ? '#dc2626' : '#1a1a2e',
  color: '#fff',
  borderRadius: '6px',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: 500,
});

export default BulkCommandModal;
